import { Listbox, Transition } from '@headlessui/react'
import { FieldProps } from 'formik'
import { Fragment } from 'react'
import { ChevronDownIcon } from '@heroicons/react/outline'

import styles from './Select.module.css'

interface SelectProps extends Partial<FieldProps> {
  className?: string
  onBlur?: (params: any) => void
  value?: any
  defaultValue?: any
  onChange: (value: any) => void
  children: React.ReactNode
  name?: string
  disabled?: boolean
}

export function Select(props: SelectProps) {
  const {
    className = '',
    children,
    form,
    value,
    defaultValue,
    onChange,
    name,
    disabled,
    onBlur,
    ...rest
  } = props

  const selectValue = value !== undefined ? value : defaultValue

  // Find the display label for the current value
  const childArray = Array.isArray(children) ? children : [children]
  let displayLabel: React.ReactNode = selectValue

  try {
    const matching = childArray.find(
      (child: any) => child?.props?.value === selectValue
    )
    const placeholder = childArray.find(
      (child: any) => child?.props?.hidden === true
    )

    if (matching?.props?.children) {
      displayLabel = matching.props.children
    } else if (placeholder?.props?.children) {
      displayLabel = placeholder.props.children
    }
  } catch (e) {
    // Fallback to raw value
    console.warn('Error finding select label:', e)
  }

  return (
    <div className={`relative ${className}`}>
      <Listbox
        value={selectValue}
        onChange={onChange}
        name={name}
        disabled={disabled}
      >
        <Listbox.Button
          className={`${styles.button} ${
            disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
          }`}
          onBlur={onBlur}
        >
          <span className="block truncate text-left">{displayLabel}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Listbox.Options
            className={`${styles.popover} absolute top-full mt-1 w-full`}
          >
            <div className={styles.list}>{children}</div>
          </Listbox.Options>
        </Transition>
      </Listbox>
    </div>
  )
}

export function Option({
  value,
  children,
  className = '',
  hidden = false
}: {
  value: any
  children: React.ReactNode
  className?: string
  hidden?: boolean
}) {
  if (hidden) {
    return null // Don't render hidden options in the dropdown
  }

  return (
    <Listbox.Option
      className={({ active, selected }) =>
        `${styles.option} ${active ? 'bg-blue-50 text-blue-900' : ''} ${
          selected ? 'text-blue-900 bg-blue-100' : ''
        } ${className}`
      }
      value={value}
    >
      {({ selected }) => (
        <span
          className={`block truncate ${
            selected ? 'font-medium' : 'font-normal'
          }`}
        >
          {children}
        </span>
      )}
    </Listbox.Option>
  )
}
